import { observer } from 'mobx-react-lite'
import { nanoid } from 'nanoid'
import { useEffect, useState } from 'react'
import { Button } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { type Integration, IntegrationsApi } from 'entities/Integrations'
import { Ampersand } from 'entities/Ampersand/ui/Ampersand/Ampersand'
import type { IAmpersandSettings } from 'entities/Ampersand'
import { SettingsRoutes } from 'pages/settings/route/type'
import { IntegrationsRoutes } from 'pages/settings/pages/integrations/route/type'
import styles from './styles.module.scss'

type IAmpersandConnectButton = {
  integration: Integration
  isIntegrationForbidden: boolean
}

export const AmpersandConnectButton = observer(
  ({ integration, isIntegrationForbidden }: IAmpersandConnectButton) => {
    const [data, setData] = useState<IAmpersandSettings | null>(null)
    const [loading, setLoading] = useState(false)

    const getAppSettings = () => IntegrationsApi.getAmpersandAppSettings(integration.key)

    useEffect(() => {
      getAppSettings()
        .then(({ data }) => setData(data))
        .catch(console.error)
    }, [integration.key])

    const onConnect = async () => {
      let settings: IAmpersandSettings | null = null
      if (data) {
        settings = data
      }
      try {
        setLoading(true)
        const res = await getAppSettings()
        settings = res.data
        setData(settings)
        if (settings) {
          const id = nanoid()
          const onSuccess = () => {
            modalStore.closeModal(id)
            uiStore.changeRoute({
              path: `/${SettingsRoutes.root}/${IntegrationsRoutes.root}/${integration.key}/${IntegrationsRoutes.settings}`,
            })
          }
          modalStore.addModal({
            title: `Connect to ${integration.name}`,
            id,
            width: 'unset',
            pureContent: true,
            ModalContent: () => (
              <div className={styles.wrap}>
                {settings && (
                  <Ampersand integration={integration} settings={settings} onSuccess={onSuccess} />
                )}
              </div>
            ),
          })
        }
      } catch (e) {
        logger.error(e)
      } finally {
        setLoading(false)
      }
    }

    return (
      <Button
        loading={loading}
        text='Connect'
        onClick={onConnect}
        iconProps={{ icon: 'plug' }}
        size='medium'
        tooltipProps={{
          visibility: isIntegrationForbidden ? 'visible' : 'hidden',
          label:
            'Disabled for HIPAA compliance. Contact Support if you need help or have questions about this integration.',
          placement: 'top',
          maxWidth: '356px',
          breakWord: true,
        }}
        disabled={isIntegrationForbidden}
      />
    )
  }
)
